import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import type { ConsolidatedEventCluster, EventResponse, SessionEvent } from '@/types';
import {
  EVENT_TYPE_LABELS,
  formatDecisionSummary,
  formatVideoTime,
} from '@/types';
import EventConsensusDialog from '@/components/review/EventConsensusDialog';
import ConsensusRateInfoIcon from '@/components/review/ConsensusRateInfoIcon';
import JudgementOutcomeLine from '@/components/review/JudgementOutcomeLine';
import EventRemarkLine from '@/components/review/EventRemarkLine';
import DecisionEventEditPopover, { type DecisionEventEditValues } from '@/components/review/DecisionEventEditPopover';
import {
  agreementMetricColor,
  findClusterIdForEventId,
  clusterHasOfficialAnswer,
  formatClusterAgreementDisplayLabel,
  getClusterOwnerAssertion,
  isParticipantAlignedWithOwner,
} from '@/components/review/consensus';
import { isOfficialReferenceEvent } from '@/components/review/officialSource';

interface Props {
  events: SessionEvent[];
  responses?: EventResponse[];
  clusters?: ConsolidatedEventCluster[];
  homeColor?: string;
  awayColor?: string;
  homeTeam?: string;
  awayTeam?: string;
  canEditOfficial?: boolean;
  /** Group managers: edit any official or participant mark. */
  canEditAllEvents?: boolean;
  /** Open per-event consensus / stats summary (owners, managers, analytics viewers). */
  canViewEventStats?: boolean;
  canEditOwnMarks?: boolean;
  canRespond?: boolean;
  selectedEventId?: string | null;
  onSelect?: (event: SessionEvent) => void;
  onSeek?: (seconds: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEditSubmit?: (event: SessionEvent, values: DecisionEventEditValues) => void | Promise<void>;
  onDelete?: (event: SessionEvent) => void;
  onRespond?: (event: SessionEvent) => void;
  currentUserId?: number;
  /** Designated official answer author (share official_source_user_id). */
  officialSourceUserId?: number;
  officialSourceUserIds?: number[];
  /** e.g. 「（我的）」 or 「（整合全體回覆）」 — appended to section titles when set. */
  timelineScopeSuffix?: string;
  showParticipantMarkers?: boolean;
  /** Owner/manager consensus clustering; off for normal participants */
  showConsensusView?: boolean;
  /** Consensus timeline is browse-only (no edit/delete on cluster rows). */
  consensusViewReadOnly?: boolean;
  /** When true, only show participant marks created by currentUserId */
  ownParticipantMarksOnly?: boolean;
  /** Participant comparison: show official marks alongside own remarks */
  showOfficialForComparison?: boolean;
  /** Participant view: show alignment with official instead of group consensus */
  participantComparisonView?: boolean;
  /** Facilitator view: designated official answer vs all participant responses (no % 共識 chip) */
  officialVsAllView?: boolean;
  /** Hide the internal section title (when parent card already shows a header) */
  hideTitle?: boolean;
  /** Stretch list rows to parent width (pro mode sidebar). */
  fullWidth?: boolean;
  /** Hide official judgement / reviewed answers for teaching */
  educationMode?: boolean;
  videoDuration?: number;
  popoverZIndex?: number;
  menuZIndex?: number;
  dialogZIndex?: number;
  /** Participant share deadline passed — no edit/delete own marks; integrated view is stats-only. */
  participantDeadlinePassed?: boolean;
}

function timelineContainerSx(fullWidth: boolean) {
  return fullWidth ? { width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' as const } : undefined;
}

function timelineListSx(fullWidth: boolean) {
  return fullWidth ? { width: '100%', maxWidth: '100%' } : undefined;
}

function timelineListItemSx(fullWidth: boolean, selected: boolean) {
  return {
    mb: 0.5,
    borderRadius: 1,
    border: selected ? '1px solid' : '1px solid transparent',
    borderColor: selected ? 'secondary.main' : 'transparent',
    bgcolor: selected ? 'rgba(139,195,74,0.08)' : 'transparent',
    boxSizing: 'border-box',
    ...(fullWidth && {
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      alignItems: 'stretch',
    }),
  };
}

/** Override theme ListItemButton mx so rows fill the parent card in pro mode. */
function timelineListItemButtonSx(fullWidth: boolean, pr: number) {
  return {
    pr,
    alignItems: 'flex-start',
    boxSizing: 'border-box' as const,
    ...(fullWidth && {
      width: '100%',
      maxWidth: '100%',
      flex: 1,
      mx: 0,
      borderRadius: 1,
    }),
  };
}

function teamChip(
  side?: string | null,
  home?: string,
  away?: string,
  homeColor?: string,
  awayColor?: string
) {
  if (!side) return null;
  const label = side === 'home' ? home || '主' : side === 'away' ? away || '客' : '中';
  const color = side === 'home' ? homeColor : side === 'away' ? awayColor : undefined;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        bgcolor: color ? `${color}33` : undefined,
        border: color ? `1px solid ${color}` : undefined,
      }}
    />
  );
}

function resolveOfficialEvent(
  cluster: ConsolidatedEventCluster,
  eventMap: Map<string, SessionEvent>,
  officialSourceUserIds?: number | number[]
): SessionEvent | undefined {
  const anchor = cluster.anchorEventId ? eventMap.get(cluster.anchorEventId) : undefined;
  const fromAssertions = cluster.assertions
    .map((a) => (a.linked_event_id ? eventMap.get(a.linked_event_id) : undefined))
    .find(
      (evt): evt is SessionEvent =>
        !!evt && isOfficialReferenceEvent(evt, officialSourceUserIds)
    );
  return (
    fromAssertions ||
    (anchor && isOfficialReferenceEvent(anchor, officialSourceUserIds) ? anchor : undefined)
  );
}

function DecisionDetailBlock({
  onFieldDecision,
  onFieldPunishment,
  reviewedDecision,
  reviewedPunishment,
  judgement,
  hideOnField,
  hideAnswers,
}: {
  onFieldDecision?: string | null;
  onFieldPunishment?: string | null;
  reviewedDecision?: string | null;
  reviewedPunishment?: string | null;
  judgement?: string | null;
  hideOnField?: boolean;
  hideAnswers?: boolean;
}) {
  const hasOnField = !hideOnField && (onFieldDecision || onFieldPunishment);

  return (
    <Stack spacing={0.35}>
      {hasOnField && (
        <Typography variant="caption" color="text.secondary">
          場上：{formatDecisionSummary(onFieldDecision, onFieldPunishment)}
        </Typography>
      )}
      {!hideAnswers && judgement && (
        <JudgementOutcomeLine
          prefix="評價："
          judgement={judgement}
          reviewedDecision={reviewedDecision}
          reviewedPunishment={reviewedPunishment}
        />
      )}
    </Stack>
  );
}

function isTimelineMarkEvent(event: SessionEvent): boolean {
  return !!(event.on_field_decision || event.judgement || event.note?.trim());
}

function eventOnFieldTitle(event: SessionEvent): string {
  if (event.on_field_decision || event.on_field_punishment) {
    return formatDecisionSummary(event.on_field_decision, event.on_field_punishment);
  }
  return EVENT_TYPE_LABELS[event.event_type as keyof typeof EVENT_TYPE_LABELS] || event.event_type;
}

function clusterOnFieldTitle(
  cluster: ConsolidatedEventCluster,
  officialEvent?: SessionEvent
): string {
  return formatDecisionSummary(
    officialEvent?.on_field_decision ?? cluster.onFieldBall,
    officialEvent?.on_field_punishment ?? cluster.dominantSanction
  );
}

function toClusterEvent(cluster: ConsolidatedEventCluster): SessionEvent {
  return {
    id: cluster.clusterId,
    session_id: 'consensus',
    session_type: 'review',
    event_type: 'decision',
    marker_role: 'official',
    video_second: cluster.anchorTimestamp,
    on_field_decision: cluster.onFieldBall,
    on_field_punishment: cluster.dominantSanction || null,
    reviewed_decision: null,
    reviewed_punishment: null,
    judgement: null,
    created_by_user_id: 0,
    created_at: cluster.clusterId,
    updated_at: cluster.clusterId,
  };
}

export default function RefereeEventTimeline({
  events,
  responses = [],
  clusters,
  homeColor,
  awayColor,
  homeTeam,
  awayTeam,
  canEditOfficial,
  canEditAllEvents = false,
  canViewEventStats = false,
  canEditOwnMarks,
  canRespond,
  selectedEventId,
  onSelect,
  onSeek,
  onPlay,
  onPause,
  onEditSubmit,
  onDelete,
  onRespond,
  currentUserId,
  officialSourceUserId,
  officialSourceUserIds,
  timelineScopeSuffix = '',
  showParticipantMarkers = true,
  showConsensusView = false,
  consensusViewReadOnly = false,
  ownParticipantMarksOnly = true,
  showOfficialForComparison = false,
  participantComparisonView = false,
  officialVsAllView = false,
  hideTitle = false,
  fullWidth = false,
  educationMode = false,
  videoDuration,
  popoverZIndex,
  menuZIndex,
  dialogZIndex,
  participantDeadlinePassed = false,
}: Props) {
  const [consensusOpen, setConsensusOpen] = useState<ConsolidatedEventCluster | null>(null);
  const [editing, setEditing] = useState<{ event: SessionEvent; anchorEl: HTMLElement } | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const eventMap = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const clusterById = useMemo(
    () => new Map((clusters ?? []).map((c) => [c.clusterId, c])),
    [clusters]
  );
  const effectiveOfficialIds = useMemo(
    () =>
      officialSourceUserIds?.length
        ? officialSourceUserIds
        : officialSourceUserId != null
          ? [officialSourceUserId]
          : undefined,
    [officialSourceUserIds, officialSourceUserId]
  );

  useEffect(() => {
    setConsensusOpen((current) => {
      if (!current) return current;
      return clusterById.get(current.clusterId) ?? current;
    });
  }, [clusterById]);

  const showAnswers = !educationMode;
  /** 整合全體回覆: cluster rows always show 事件統計 only (edit via stats dialog / 共識比對表). */
  const integratedConsensusRowStatsOnly = showConsensusView;
  const showStatsIcon = showConsensusView;
  const allowOwnMarkEdit = !!canEditOwnMarks && !participantDeadlinePassed;
  const allowClusterRowActions =
    !consensusViewReadOnly &&
    !integratedConsensusRowStatsOnly &&
    !participantDeadlinePassed;

  const clusterForEventId = (eventId: string): ConsolidatedEventCluster | null => {
    if (!clusters?.length) return null;
    const clusterId = findClusterIdForEventId(clusters, eventId);
    return clusterId ? clusterById.get(clusterId) ?? null : null;
  };

  const consensusDialog = showStatsIcon ? (
    <EventConsensusDialog
      open={!!consensusOpen}
      cluster={consensusOpen}
      onClose={() => setConsensusOpen(null)}
      onJumpToTime={(seconds, cluster) => {
        onSelect?.(toClusterEvent(cluster));
        onSeek?.(seconds);
        onPlay?.();
      }}
      educationMode={educationMode}
      officialSourceUserId={officialSourceUserId}
      officialSourceUserIds={effectiveOfficialIds}
      events={events}
      canEditEvents={canEditAllEvents}
      onEditEvent={async (eventId, values) => {
        const event = events.find((e) => e.id === eventId);
        if (event) await onEditSubmit?.(event, values);
      }}
      videoDuration={videoDuration}
      zIndex={dialogZIndex}
      menuZIndex={menuZIndex}
    />
  ) : null;

  const openConsensus = (cluster: ConsolidatedEventCluster) => {
    onPause?.();
    setConsensusOpen(cluster);
  };

  const openEdit = (event: SessionEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    onPause?.();
    const card = (e.currentTarget as HTMLElement).closest('[data-timeline-event-card]') as HTMLElement | null;
    if (card) setEditing({ event, anchorEl: card });
  };

  const editPopover = (
    <DecisionEventEditPopover
      key={editing?.event.id ?? 'closed'}
      open={!!editing}
      anchorEl={editing?.anchorEl ?? null}
      initial={editing?.event}
      showVideoTime
      videoDuration={videoDuration}
      onPreviewTime={(s) => onSeek?.(Math.max(0, s - 0.8))}
      popoverZIndex={popoverZIndex}
      menuZIndex={menuZIndex}
      dark={isDark}
      onClose={() => setEditing(null)}
      onSubmit={async (values) => {
        if (editing) await onEditSubmit?.(editing.event, values);
        setEditing(null);
      }}
    />
  );

  if (showConsensusView && clusters && clusters.length > 0) {
    return (
      <Box sx={timelineContainerSx(fullWidth)}>
        {!hideTitle && (
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.5 }}>
            <Typography variant="overline" color="text.secondary">
              判決時間軸{timelineScopeSuffix} ({clusters.length})
            </Typography>
            <ConsensusRateInfoIcon iconSize={14} />
          </Stack>
        )}
        <List dense disablePadding sx={timelineListSx(fullWidth)}>
          {clusters.map((cluster) => {
            const anchorEvent = cluster.anchorEventId ? eventMap.get(cluster.anchorEventId) : undefined;
            const officialEvent = resolveOfficialEvent(cluster, eventMap, effectiveOfficialIds);
            const selected =
              selectedEventId === cluster.clusterId ||
              (anchorEvent != null && selectedEventId === anchorEvent.id);
            const eventForActions = officialEvent || anchorEvent || null;
            const isOfficialAnchor = eventForActions?.marker_role !== 'participant';
            const canEdit =
              allowClusterRowActions &&
              !!(eventForActions && (canEditAllEvents || (isOfficialAnchor && canEditOfficial)));
            const canRespondNow =
              allowClusterRowActions &&
              !!(eventForActions && canRespond && eventForActions.marker_role !== 'participant');
            const clusterActionCount =
              (showStatsIcon ? 1 : 0) + (canRespondNow ? 1 : 0) + (canEdit ? 2 : 0);
            const eventForSelection = eventForActions || toClusterEvent(cluster);
            const agreementTone = agreementMetricColor(cluster.agreementRate ?? 0, isDark);
            const ownerAssertion = getClusterOwnerAssertion(cluster, effectiveOfficialIds);
            const hasOfficialAnswer = clusterHasOfficialAnswer(cluster, effectiveOfficialIds);
            const responseCount = cluster.assertions.filter((a) => a.role !== 'owner').length;
            const myAligned =
              participantComparisonView && currentUserId != null
                ? isParticipantAlignedWithOwner(cluster, currentUserId, effectiveOfficialIds)
                : null;
            const statusTone =
              participantComparisonView && myAligned != null
                ? agreementMetricColor(myAligned ? 100 : 0, isDark)
                : agreementTone;

            return (
              <ListItem
                key={cluster.clusterId}
                disablePadding
                data-timeline-event-card
                sx={timelineListItemSx(fullWidth, selected)}
                secondaryAction={
                  <Stack direction="row">
                    {showStatsIcon && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openConsensus(cluster);
                        }}
                        title="事件統計"
                      >
                        <AnalyticsOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canRespondNow && (
                      <IconButton
                        size="small"
                        onClick={() => eventForActions && onRespond?.(eventForActions)}
                        title="作答"
                      >
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canEdit && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            if (eventForActions) openEdit(eventForActions, e);
                          }}
                          title="編輯錨點事件"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => eventForActions && onDelete?.(eventForActions)}
                          title="刪除錨點事件"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                }
              >
                <ListItemButton
                  onClick={() => {
                    onSelect?.(eventForSelection);
                    onSeek?.(Math.max(0, cluster.anchorTimestamp - 0.8));
                  }}
                  sx={timelineListItemButtonSx(fullWidth, clusterActionCount > 0 ? 12 : 2)}
                >
                  <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={formatVideoTime(cluster.anchorTimestamp)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
                        {clusterOnFieldTitle(cluster, officialEvent)}
                      </Typography>
                      {hasOfficialAnswer ? (
                        <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                      ) : (
                        <GroupsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      )}
                      {participantComparisonView ? (
                        showAnswers && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              myAligned == null
                                ? '未標記'
                                : myAligned
                                  ? '與官方一致'
                                  : '與官方分歧'
                            }
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: statusTone.bg,
                              color: statusTone.color,
                              borderColor: statusTone.border,
                            }}
                          />
                        )
                      ) : officialVsAllView ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="secondary"
                          label="官方"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={formatClusterAgreementDisplayLabel(cluster, effectiveOfficialIds)}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: agreementTone.bg,
                            color: agreementTone.color,
                            borderColor: agreementTone.border,
                          }}
                        />
                      )}
                    </Stack>
                    <DecisionDetailBlock
                      hideOnField
                      hideAnswers={!showAnswers}
                      onFieldDecision={officialEvent?.on_field_decision ?? cluster.onFieldBall}
                      onFieldPunishment={officialEvent?.on_field_punishment ?? cluster.dominantSanction}
                      reviewedDecision={officialEvent?.reviewed_decision}
                      reviewedPunishment={officialEvent?.reviewed_punishment}
                      judgement={officialEvent?.judgement}
                    />
                    <EventRemarkLine note={officialEvent?.note} dark={isDark} />
                    <Typography variant="caption" color="text.secondary">
                      {participantComparisonView
                        ? myAligned == null
                          ? '您尚未在此事件標記'
                          : `您的判決 · 平均偏差 Δt ${(cluster.averageVariance || 0).toFixed(2)} 秒`
                        : officialVsAllView
                          ? `${responseCount} 人回覆 · 官方${ownerAssertion ? '判決' : '基準'} · Δt ${(cluster.averageVariance || 0).toFixed(2)} 秒`
                          : `${cluster.assertions.length} 人標記 · 平均偏差 Δt ${(cluster.averageVariance || 0).toFixed(2)} 秒`}
                    </Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        {consensusDialog}
        {editPopover}
      </Box>
    );
  }

  if (ownParticipantMarksOnly) {
    const ownMarks = events
      .filter((e) => {
        if (!isTimelineMarkEvent(e)) return false;
        if (currentUserId == null) return false;
        return Number(e.created_by_user_id) === Number(currentUserId);
      })
      .sort((a, b) => (a.video_second ?? 0) - (b.video_second ?? 0));

    const responseMap = new Map<string, EventResponse>();
    for (const r of responses) {
      if (currentUserId && r.user_id === currentUserId) {
        responseMap.set(r.official_event_id, r);
      }
    }

    if (ownMarks.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>尚無個人標記</Typography>
          <Typography variant="caption">使用右側面板於影片時間軸加入判決標記</Typography>
        </Box>
      );
    }

    const renderOwnMark = (event: SessionEvent) => {
      const isOfficial = isOfficialReferenceEvent(event, effectiveOfficialIds);
      const selected = selectedEventId === event.id;
      const myResponse = isOfficial ? responseMap.get(event.id) : undefined;
      const typeLabel = eventOnFieldTitle(event);
      const isOwn = currentUserId != null && Number(event.created_by_user_id) === Number(currentUserId);
      const canEditMark =
        (isOfficial && (canEditOfficial || canEditAllEvents)) ||
        (!isOfficial && (canEditAllEvents || (allowOwnMarkEdit && isOwn)));

      return (
        <ListItem
          key={event.id}
          disablePadding
          data-timeline-event-card
          sx={timelineListItemSx(fullWidth, selected)}
          secondaryAction={
            canEditMark ? (
              <Stack direction="row">
                <IconButton size="small" onClick={(e) => openEdit(event, e)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onDelete?.(event)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : undefined
          }
        >
          <ListItemButton
            onClick={() => {
              onSelect?.(event);
              if (event.video_second != null) onSeek?.(Math.max(0, event.video_second - 0.8));
            }}
            sx={timelineListItemButtonSx(fullWidth, canEditMark ? 12 : 2)}
          >
            <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip label={formatVideoTime(event.video_second ?? 0)} size="small" color="secondary" variant="outlined" />
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
                  {typeLabel}
                </Typography>
                {isOfficial ? (
                  <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                )}
                {teamChip(event.team_side, homeTeam, awayTeam, homeColor, awayColor)}
              </Stack>
              <DecisionDetailBlock
                hideOnField
                hideAnswers={isOfficial && !showAnswers}
                onFieldDecision={event.on_field_decision}
                onFieldPunishment={event.on_field_punishment}
                reviewedDecision={event.reviewed_decision}
                reviewedPunishment={event.reviewed_punishment}
                judgement={event.judgement}
              />
              <EventRemarkLine note={event.note} dark={isDark} />
              {myResponse && (
                <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'info.light' }}>
                  <Typography variant="caption" color="info.main" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
                    我的作答
                  </Typography>
                  <DecisionDetailBlock
                    onFieldDecision={myResponse.on_field_decision}
                    onFieldPunishment={myResponse.on_field_punishment}
                    reviewedDecision={myResponse.reviewed_decision}
                    reviewedPunishment={myResponse.reviewed_punishment}
                    judgement={myResponse.judgement}
                  />
                  <EventRemarkLine note={myResponse.note} dark={isDark} />
                </Box>
              )}
            </Stack>
          </ListItemButton>
        </ListItem>
      );
    };

    return (
      <Box sx={timelineContainerSx(fullWidth)}>
        {!hideTitle && (
          <Typography variant="overline" color="text.secondary">
            我的標記{timelineScopeSuffix} ({ownMarks.length})
          </Typography>
        )}
        <List dense disablePadding sx={timelineListSx(fullWidth)}>
          {ownMarks.map((e) => renderOwnMark(e))}
        </List>
        {editPopover}
      </Box>
    );
  }

  const official = events.filter((e) => e.marker_role !== 'participant');
  const showParticipantList = showParticipantMarkers;
  const participant = showParticipantList
    ? events.filter((e) => e.marker_role === 'participant')
    : [];

  const responseMap = new Map<string, EventResponse>();
  for (const r of responses) {
    if (currentUserId && r.user_id === currentUserId) {
      responseMap.set(r.official_event_id, r);
    }
  }

  if (official.length === 0 && participant.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography>{ownParticipantMarksOnly ? '尚無個人標記' : '尚無判決標記'}</Typography>
        <Typography variant="caption">
          {ownParticipantMarksOnly
            ? '使用右側面板於影片時間軸加入判決標記'
            : '使用右側面板於影片時間軸加入官方標記'}
        </Typography>
      </Box>
    );
  }

  const renderEvent = (event: SessionEvent, isOfficialRole: boolean) => {
    const showAsOfficial = isOfficialRole || isOfficialReferenceEvent(event, effectiveOfficialIds);
    const selected = selectedEventId === event.id;
    const myResponse = isOfficialRole ? responseMap.get(event.id) : undefined;
    const typeLabel = eventOnFieldTitle(event);

    const isOwn =
      currentUserId != null && Number(event.created_by_user_id) === Number(currentUserId);
    const canEditMark =
      (isOfficialRole && (canEditOfficial || canEditAllEvents)) ||
      (!isOfficialRole && (canEditAllEvents || (canEditOwnMarks && isOwn)));
    const eventCluster = showStatsIcon ? clusterForEventId(event.id) : null;

    return (
      <ListItem
        key={event.id}
        disablePadding
        data-timeline-event-card
        sx={timelineListItemSx(fullWidth, selected)}
        secondaryAction={
          <Stack direction="row">
            {showStatsIcon && eventCluster && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openConsensus(eventCluster);
                }}
                title="事件統計"
              >
                <AnalyticsOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            {canRespond && isOfficialRole && (
              <IconButton size="small" onClick={() => onRespond?.(event)} title="作答">
                <ReplyIcon fontSize="small" />
              </IconButton>
            )}
            {canEditMark ? (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => openEdit(event, e)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onDelete?.(event)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            ) : null}
          </Stack>
        }
      >
        <ListItemButton
          onClick={() => {
            onSelect?.(event);
            if (event.video_second != null) onSeek?.(Math.max(0, event.video_second - 0.8));
          }}
          sx={timelineListItemButtonSx(
            fullWidth,
            canEditMark || (canRespond && isOfficialRole) || (showStatsIcon && eventCluster) ? 12 : 2
          )}
        >
          <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Chip label={formatVideoTime(event.video_second ?? 0)} size="small" color="secondary" variant="outlined" />
              <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
                {typeLabel}
              </Typography>
              {showAsOfficial ? (
                <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
              ) : (
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              )}
              {teamChip(event.team_side, homeTeam, awayTeam, homeColor, awayColor)}
            </Stack>
            <DecisionDetailBlock
              hideOnField
              hideAnswers={showAsOfficial && !showAnswers}
              onFieldDecision={event.on_field_decision}
              onFieldPunishment={event.on_field_punishment}
              reviewedDecision={event.reviewed_decision}
              reviewedPunishment={event.reviewed_punishment}
              judgement={event.judgement}
            />
            <EventRemarkLine note={event.note} dark={isDark} />
            {myResponse && (
              <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'info.light' }}>
                <Typography variant="caption" color="info.main" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
                  我的作答
                </Typography>
                <DecisionDetailBlock
                  onFieldDecision={myResponse.on_field_decision}
                  onFieldPunishment={myResponse.on_field_punishment}
                  reviewedDecision={myResponse.reviewed_decision}
                  reviewedPunishment={myResponse.reviewed_punishment}
                  judgement={myResponse.judgement}
                />
                <EventRemarkLine note={myResponse.note} dark={isDark} />
              </Box>
            )}
          </Stack>
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={timelineContainerSx(fullWidth)}>
      {official.length > 0 && (
        <>
          <Typography variant="overline" color="text.secondary">
            {showOfficialForComparison
              ? `官方判決${timelineScopeSuffix} (${official.length})`
              : `官方標記${timelineScopeSuffix} (${official.length})`}
          </Typography>
          <List dense disablePadding sx={timelineListSx(fullWidth)}>
            {official.map((e) => renderEvent(e, true))}
          </List>
        </>
      )}
      {participant.length > 0 && (
        <>
          {official.length > 0 && <Divider sx={{ my: 1.5 }} />}
          <Typography variant="overline" color="text.secondary">
            {ownParticipantMarksOnly
              ? `我的標記${timelineScopeSuffix} (${participant.length})`
              : `參與者標記${timelineScopeSuffix} (${participant.length})`}
          </Typography>
          <List dense disablePadding sx={timelineListSx(fullWidth)}>
            {participant.map((e) => renderEvent(e, false))}
          </List>
        </>
      )}
      {consensusDialog}
      {editPopover}
    </Box>
  );
}

export function TeamScoreboard({
  homeTeam,
  awayTeam,
  homeColor = '#8bc34a',
  awayColor = '#2196f3',
}: {
  homeTeam?: string | null;
  awayTeam?: string | null;
  homeColor?: string;
  awayColor?: string;
}) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 8, height: 32, borderRadius: 1, bgcolor: homeColor, flexShrink: 0 }} />
            <Typography fontWeight={700} noWrap>
              {homeTeam || '主隊'}
            </Typography>
          </Stack>
          <Typography color="text.secondary" fontWeight={600}>
            VS
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
            <Typography fontWeight={700} noWrap textAlign="right">
              {awayTeam || '客隊'}
            </Typography>
            <Box sx={{ width: 8, height: 32, borderRadius: 1, bgcolor: awayColor, flexShrink: 0 }} />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
